// Integration Configuration Schemas for Different Providers

export const INTEGRATION_CONFIGS = {
  // TICKETING INTEGRATIONS
  'ServiceNow': {
    fields: [
      {
        key: 'instance_url',
        label: 'ServiceNow Instance URL',
        type: 'url',
        placeholder: 'https://your-instance.service-now.com',
        required: true,
        description: 'Your ServiceNow instance URL'
      },
      {
        key: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'service-account-user',
        required: true,
        description: 'ServiceNow username for API access'
      },
      {
        key: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        description: 'Password for ServiceNow user'
      },
      {
        key: 'table_name',
        label: 'Default Table',
        type: 'select',
        options: ['incident', 'change_request', 'problem', 'task'],
        defaultValue: 'incident',
        required: true,
        description: 'Default table to create tickets in'
      },
      {
        key: 'assignment_group',
        label: 'Assignment Group',
        type: 'text',
        placeholder: 'IT Operations',
        required: false,
        description: 'Default assignment group for created tickets'
      }
    ],
    endpoints: {
      base_url: '${instance_url}',
      api_endpoint: '${instance_url}/api/now/table/${table_name}',
      health_check: '${instance_url}/api/now/table/sys_user?sysparm_limit=1'
    },
    credentials_keys: ['username', 'password']
  },

  'Jira': {
    fields: [
      {
        key: 'jira_url',
        label: 'Jira URL',
        type: 'url',
        placeholder: 'https://your-domain.atlassian.net',
        required: true,
        description: 'Your Jira instance URL'
      },
      {
        key: 'email',
        label: 'Email',
        type: 'email',
        placeholder: 'user@example.com',
        required: true,
        description: 'Email associated with your Jira account'
      },
      {
        key: 'api_token',
        label: 'API Token',
        type: 'password',
        placeholder: '••••••••••••••••',
        required: true,
        description: 'Jira API token (generated from Account Settings)'
      },
      {
        key: 'project_key',
        label: 'Default Project Key',
        type: 'text',
        placeholder: 'PROJ',
        required: true,
        description: 'Default project key for creating issues'
      },
      {
        key: 'issue_type',
        label: 'Default Issue Type',
        type: 'select',
        options: ['Bug', 'Task', 'Story', 'Epic', 'Incident'],
        defaultValue: 'Bug',
        required: true,
        description: 'Default issue type for created tickets'
      }
    ],
    endpoints: {
      base_url: '${jira_url}',
      api_endpoint: '${jira_url}/rest/api/3/issue',
      health_check: '${jira_url}/rest/api/3/myself'
    },
    credentials_keys: ['email', 'api_token']
  },

  'Zendesk': {
    fields: [
      {
        key: 'subdomain',
        label: 'Zendesk Subdomain',
        type: 'text',
        placeholder: 'your-company',
        required: true,
        description: 'Your Zendesk subdomain (from your-company.zendesk.com)'
      },
      {
        key: 'email',
        label: 'Email',
        type: 'email',
        placeholder: 'user@example.com',
        required: true,
        description: 'Email of the Zendesk agent'
      },
      {
        key: 'api_token',
        label: 'API Token',
        type: 'password',
        placeholder: '••••••••••••••••',
        required: true,
        description: 'Zendesk API token'
      },
      {
        key: 'default_priority',
        label: 'Default Priority',
        type: 'select',
        options: ['low', 'normal', 'high', 'urgent'],
        defaultValue: 'normal',
        required: true,
        description: 'Default priority for created tickets'
      }
    ],
    endpoints: {
      base_url: 'https://${subdomain}.zendesk.com',
      api_endpoint: 'https://${subdomain}.zendesk.com/api/v2/tickets',
      health_check: 'https://${subdomain}.zendesk.com/api/v2/users/me'
    },
    credentials_keys: ['email', 'api_token']
  },

  // NOTIFICATION INTEGRATIONS
  'Slack': {
    fields: [
      {
        key: 'workspace_url',
        label: 'Workspace URL',
        type: 'url',
        placeholder: 'https://your-workspace.slack.com',
        required: true,
        description: 'Your Slack workspace URL'
      },
      {
        key: 'bot_token',
        label: 'Bot User OAuth Token',
        type: 'password',
        placeholder: 'xoxb-••••••••••••••••',
        required: true,
        description: 'Bot token starting with xoxb-'
      },
      {
        key: 'default_channel',
        label: 'Default Channel',
        type: 'text',
        placeholder: '#alerts',
        required: true,
        description: 'Default channel for notifications (include #)'
      },
      {
        key: 'username',
        label: 'Bot Username',
        type: 'text',
        placeholder: 'NS2 NimbusMind Bot',
        required: false,
        description: 'Display name for the bot'
      }
    ],
    endpoints: {
      base_url: 'https://slack.com/api',
      api_endpoint: 'https://slack.com/api/chat.postMessage',
      health_check: 'https://slack.com/api/auth.test'
    },
    credentials_keys: ['bot_token']
  },

  'Microsoft Teams': {
    fields: [
      {
        key: 'webhook_url',
        label: 'Incoming Webhook URL',
        type: 'url',
        placeholder: 'https://outlook.office.com/webhook/...',
        required: true,
        description: 'Teams incoming webhook URL'
      },
      {
        key: 'team_name',
        label: 'Team Name',
        type: 'text',
        placeholder: 'Operations Team',
        required: false,
        description: 'Name of the team for reference'
      }
    ],
    endpoints: {
      base_url: '${webhook_url}',
      api_endpoint: '${webhook_url}',
      health_check: '${webhook_url}'
    },
    credentials_keys: ['webhook_url']
  },

  // MONITORING INTEGRATIONS
  'Datadog': {
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'password',
        placeholder: '••••••••••••••••••••••••••••••••',
        required: true,
        description: 'Datadog API key'
      },
      {
        key: 'app_key',
        label: 'Application Key',
        type: 'password',
        placeholder: '••••••••••••••••••••••••••••••••••••••••',
        required: true,
        description: 'Datadog application key'
      },
      {
        key: 'site',
        label: 'Datadog Site',
        type: 'select',
        options: ['datadoghq.com', 'datadoghq.eu', 'us3.datadoghq.com', 'us5.datadoghq.com'],
        defaultValue: 'datadoghq.com',
        required: true,
        description: 'Datadog site region'
      }
    ],
    endpoints: {
      base_url: 'https://api.${site}',
      api_endpoint: 'https://api.${site}/api/v1/metrics',
      health_check: 'https://api.${site}/api/v1/validate'
    },
    credentials_keys: ['api_key', 'app_key']
  },

  'New Relic': {
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'password',
        placeholder: 'NRAK-••••••••••••••••••••••••••••••••••••••••',
        required: true,
        description: 'New Relic API key'
      },
      {
        key: 'account_id',
        label: 'Account ID',
        type: 'text',
        placeholder: '1234567',
        required: true,
        description: 'New Relic account ID'
      },
      {
        key: 'region',
        label: 'Region',
        type: 'select',
        options: ['US', 'EU'],
        defaultValue: 'US',
        required: true,
        description: 'New Relic region'
      }
    ],
    endpoints: {
      base_url: 'https://api.${region === "EU" ? "eu." : ""}newrelic.com',
      api_endpoint: 'https://api.${region === "EU" ? "eu." : ""}newrelic.com/v2/applications.json',
      health_check: 'https://api.${region === "EU" ? "eu." : ""}newrelic.com/v2/applications.json'
    },
    credentials_keys: ['api_key']
  },

  // ALERTING INTEGRATIONS
  'PagerDuty': {
    fields: [
      {
        key: 'api_token',
        label: 'API Token',
        type: 'password',
        placeholder: '••••••••••••••••••••',
        required: true,
        description: 'PagerDuty API token'
      },
      {
        key: 'integration_key',
        label: 'Integration Key',
        type: 'password',
        placeholder: '••••••••••••••••••••••••••••••••',
        required: true,
        description: 'PagerDuty service integration key'
      },
      {
        key: 'service_id',
        label: 'Service ID',
        type: 'text',
        placeholder: 'P123ABC',
        required: true,
        description: 'PagerDuty service ID'
      }
    ],
    endpoints: {
      base_url: 'https://api.pagerduty.com',
      api_endpoint: 'https://events.pagerduty.com/v2/enqueue',
      health_check: 'https://api.pagerduty.com/services'
    },
    credentials_keys: ['api_token', 'integration_key']
  },

  // CLOUD PROVIDER INTEGRATIONS
  'AWS': {
    fields: [
      {
        key: 'access_key_id',
        label: 'Access Key ID',
        type: 'text',
        placeholder: 'AKIA••••••••••••••••',
        required: true,
        description: 'AWS Access Key ID'
      },
      {
        key: 'secret_access_key',
        label: 'Secret Access Key',
        type: 'password',
        placeholder: '••••••••••••••••••••••••••••••••••••••••',
        required: true,
        description: 'AWS Secret Access Key'
      },
      {
        key: 'region',
        label: 'Default Region',
        type: 'select',
        options: ['us-east-1', 'us-west-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 'ap-southeast-1'],
        defaultValue: 'us-east-1',
        required: true,
        description: 'Default AWS region'
      },
      {
        key: 'session_token',
        label: 'Session Token (Optional)',
        type: 'password',
        placeholder: '••••••••••••••••••••••••••••••••••••••••',
        required: false,
        description: 'AWS Session Token (if using temporary credentials)'
      }
    ],
    endpoints: {
      base_url: 'https://ec2.${region}.amazonaws.com',
      api_endpoint: 'https://cloudwatch.${region}.amazonaws.com',
      health_check: 'https://ec2.${region}.amazonaws.com/?Action=DescribeRegions'
    },
    credentials_keys: ['access_key_id', 'secret_access_key', 'session_token']
  }
};

// Helper function to get configuration for a provider
export const getProviderConfig = (provider) => {
  return INTEGRATION_CONFIGS[provider] || null;
};

// Helper function to validate configuration
export const validateConfig = (provider, config) => {
  const providerConfig = getProviderConfig(provider);
  if (!providerConfig) return { valid: false, errors: ['Unknown provider'] };
  
  const errors = [];
  
  providerConfig.fields.forEach(field => {
    if (field.required && (!config[field.key] || config[field.key].trim() === '')) {
      errors.push(`${field.label} is required`);
    }
  });
  
  return {
    valid: errors.length === 0,
    errors
  };
};

// Helper function to process configuration for API storage
export const processConfigForStorage = (provider, config) => {
  const providerConfig = getProviderConfig(provider);
  if (!providerConfig) return { config: {}, credentials: {}, endpoints: {} };
  
  const credentials = {};
  const processedConfig = {};
  const endpoints = {};
  
  // Separate credentials from regular config
  Object.keys(config).forEach(key => {
    if (providerConfig.credentials_keys.includes(key)) {
      credentials[key] = config[key];
    } else {
      processedConfig[key] = config[key];
    }
  });
  
  // Process endpoint templates with config values
  Object.keys(providerConfig.endpoints).forEach(endpointKey => {
    let endpoint = providerConfig.endpoints[endpointKey];
    
    // Replace template variables with actual values
    Object.keys(config).forEach(configKey => {
      const regex = new RegExp(`\\$\\{${configKey}\\}`, 'g');
      endpoint = endpoint.replace(regex, config[configKey]);
    });
    
    endpoints[endpointKey] = endpoint;
  });
  
  return {
    config: processedConfig,
    credentials,
    endpoints
  };
};